import React, { useState, useEffect, useCallback } from "react";
import { Formik } from "formik";
import Input from "../../components/input";
import ValidationFeedback from "../../components/validationFeedback";
import FileInput from "../../components/fileInput";
import TextArea from "../../components/commonTextArea";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import CommonButton from "../../components/commonButton";
import { updateBlog, getBlogDetails, getTags } from "../../modules/Blog/blogSlice";
import { addTag } from "../../modules/Tag/tagSlice";
import { useLoader } from "../../context/LoaderContext";
import { blogUpdateSchema } from "../../schema/validationSchemas";
import CommonFormLabel from "../../components/formLabel";
import { Toast, Form, Row, Col, Card } from "react-bootstrap";
import Editor from "../../components/commonEditor";
import MultiSelectDropdown from "../../components/multiSelectDropdown";
import { useDispatch } from "react-redux";

export default function BlogEditNew() {
  const { id: blogId } = useParams();
  const navigate = useNavigate();
  const { setLoading } = useLoader();
  const dispatch = useDispatch();

  const location = useLocation();
  const pathSegments = location.pathname.split("/").filter(Boolean);
  const selected_organization_id = pathSegments.length > 1 && pathSegments[0] === "organization" 
    ? pathSegments[1] 
    : null;

  const [blog, setBlog] = useState(null);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarVariant, setSnackbarVariant] = useState("success");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [newImagePreview, setNewImagePreview] = useState(null); // State for new selected image preview
  // const [newVideoPreview, setNewVideoPreview] = useState(null); // State for new selected video preview
  const [tagsOptions, setTagsOptions] = useState([]);
  const params = new URLSearchParams(location.search);
  const currentPage = params.get("page") || 1;

  const handleCreateTag = async (tagName) => {
    try {
      const values = { 
        name: tagName,
        organization_id: selected_organization_id, 
      };
      const response = await dispatch(addTag(values));
      if (response.payload && response.payload.success) {
        return { value: response.payload.data.id, label: response.payload.data.name };
      } else {
        throw new Error(response.data.message || "Failed to create tag.");
      }
    } catch (error) {
      console.error("Error creating tag:", error);
      throw error;
    }
  };

  const fetchTags = useCallback(async () => {
    try {
      if (selected_organization_id) {
        const payload = {
          organization_id: selected_organization_id,
        };
        await dispatch(getTags({ payload })).then((res) => {
          if (res.payload) {
            if (res.payload.length > 0) {
              const formattedData = res.payload.map((legalFirm) => ({
                value: legalFirm.id,
                label: legalFirm.name,
              }));
              setTagsOptions(formattedData);
            }
          } else {
            setTagsOptions([]);
          }
        });
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  }, [dispatch, selected_organization_id]);

  useEffect(() => {
    fetchTags();
  }, [fetchTags]);

  useEffect(() => {
    const fetchBlog = async () => {
      setLoading(true);
      try {
        const result = await dispatch(getBlogDetails(blogId)).unwrap();
        setBlog(result.data);
      } catch (error) {
        setLoading(false);
        console.error("Error fetching blog:", error);
        setSnackbarMessage("Failed to load blog details.");
        setSnackbarVariant("danger");
        setShowSnackbar(true);
      } finally {
        setLoading(false);
      }
    };

    if (blogId) fetchBlog();
  }, [blogId, dispatch, setLoading]);

  const handleCancel = () => {
    navigate(`/organization/${selected_organization_id}/blogs?page=${currentPage}`);
  };

  const handleImageChange = (file, setFieldValue) => {
    if (file) {
      setNewImagePreview(URL.createObjectURL(file)); // Generate new image preview
      setFieldValue("blog_image", file); // Update Formik's blog_image field
    }
  };

  // const handleVideoChange = (file, setFieldValue) => {
  //   if (file) {
  //     setNewVideoPreview(URL.createObjectURL(file)); // Generate new video preview
  //     setFieldValue("blog_video", file); // Update Formik's blog_video field
  //   }
  // };

  const handleSubmit = async (values, { setSubmitting, setErrors }) => {
    setIsSubmitting(true);

    const formData = new FormData();
    formData.append("id", blogId);
    formData.append("organization_id", selected_organization_id);
    formData.append("_method", "PUT");
    formData.append("blog_title", values.blog_title);
    formData.append("video_link", values.video_link);
    formData.append("blog_slug", values.blog_slug);
    formData.append("blog_description", values.blog_description);
    formData.append("meta_title", values.meta_title);
    formData.append("meta_description", values.meta_description);
    formData.append("meta_keyword", values.meta_keyword);
    formData.append("author", values.author);
    // formData.append("tag_ids", values.tag_ids);
    values.tag_ids.forEach(tagId => {
      formData.append("tag_ids[]", tagId);
  });
    if (values.blog_image) formData.append("blog_image", values.blog_image);
    // if (values.blog_video) formData.append("blog_video", values.blog_video);

    try {
      const res = await dispatch(updateBlog(formData)).unwrap();
      if (res.success) {
        localStorage.setItem("blog_notify_message", res.message);
        setSnackbarMessage(res.message);
        setSnackbarVariant("success");
        setShowSnackbar(true);
        navigate(`/organization/${selected_organization_id}/blogs?page=${currentPage}`);
      } else {
        setErrors(res.data);
        setSnackbarMessage(res.message);
        setSnackbarVariant("danger");
        setShowSnackbar(true);
      }
    } catch (error) {
      setSnackbarMessage("Failed to update blog!");
      setSnackbarVariant("danger");
      setShowSnackbar(true);
    } finally {
      setSubmitting(false);
      setIsSubmitting(false);
    }
  };

  if (!blog)
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "60vh" }}
      >
        <h2>Loading Blog Details...</h2>
      </div>
    );

  return (
    <>
      <Toast
        onClose={() => setShowSnackbar(false)}
        show={showSnackbar}
        delay={3000}
        autohide
        bg={snackbarVariant}
        style={{ position: "fixed", top: 20, right: 20, zIndex: 1000 }}
      >
        <Toast.Body style={{ color: "white" }}>{snackbarMessage}</Toast.Body>
      </Toast>

      <Formik
        initialValues={{
          blog_title: blog?.blog_title || "",
          video_link: blog?.video_link || "",
          blog_slug: blog?.blog_slug || "",
          author: blog?.author || "",
          blog_image: "",
          // blog_video: "",
          blog_description: blog?.blog_description || "",
          tag_ids: blog?.tags?.map((tag) => tag.id) || [],
          meta_title: blog?.meta_title || "",
          meta_description: blog?.meta_description || "",
          meta_keyword: blog?.meta_keyword || ""
        }}
        validationSchema={blogUpdateSchema}
        onSubmit={handleSubmit}
        enableReinitialize
      >
        {({
          errors,
          touched,
          handleChange,
          handleSubmit,
          values,
          setFieldValue,
        }) => (
          <Form
            className="bg-white p-4 rounded-2 shadow-sm"
            onSubmit={handleSubmit}
            encType="multipart/form-data"
          >
            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Basic</h4>
                <Row>
                <Col md={6} className="mb-4">
                    <CommonFormLabel htmlFor="blog_title" title="Blog Title" />
                    <Input
                    id="blog_title"
                    name="blog_title"
                    type="text"
                    placeholder="Enter Blog Title"
                    onChange={handleChange}
                    value={values.blog_title}
                    className={`shadow-none ${
                        errors?.blog_title && touched.blog_title ? "error" : "correct"
                    }`}
                    />
                    {touched.blog_title && errors?.blog_title && (
                    <ValidationFeedback title={errors?.blog_title} />
                    )}
                </Col>

                <Col md={6} className="mb-4">
                    <CommonFormLabel htmlFor="author" title="Author" />
                    <Input
                    id="author"
                    name="author"
                    type="text"
                    placeholder="Enter Author Name"
                    onChange={handleChange}
                    value={values.author}
                    className={`shadow-none ${
                        errors?.author && touched.author ? "error" : "correct"
                    }`}
                    />
                    {touched.author && errors?.author && (
                    <ValidationFeedback title={errors?.author} />
                    )}
                </Col>

                <Col md={6} className="mb-4">
                    <CommonFormLabel htmlFor="blog_slug" title="Blog Slug" />
                    <Input
                    id="blog_slug"
                    name="blog_slug"
                    type="text"
                    placeholder="Enter Blog Slug"
                    onChange={handleChange}
                    value={values.blog_slug}
                    className={`shadow-none ${
                        errors?.blog_slug && touched.blog_slug ? "error" : "correct"
                    }`}
                    />
                    {touched.blog_slug && errors?.blog_slug && (
                    <ValidationFeedback title={errors?.blog_slug} />
                    )}
                </Col>

                <Col md={12} className="mb-4">
                  <MultiSelectDropdown
                    label="Tags"
                    name="tag_ids"
                    value={values.tag_ids}
                    onChange={(selectedOptions) => setFieldValue("tag_ids", selectedOptions)}
                    options={tagsOptions}
                    onCreateTag={handleCreateTag}
                    placeholder="Select or create tags"
                    error={errors?.tag_ids}
                    touched={touched.tag_ids}
                    module="blog"
                  />
                </Col>
                </Row>

                <div className="mb-4">
                <CommonFormLabel htmlFor="blog_description" title="Description" />
                <Editor
                    value={values.blog_description}
                    onChange={(content) =>
                    setFieldValue("blog_description", content)
                    }
                    height="250px"
                />
                {touched.blog_description && errors?.blog_description && (
                    <ValidationFeedback title={errors?.blog_description} />
                )}
                </div>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
              <h4 className="mb-4 d-flex justify-content-center">
                Meta Content
              </h4>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel htmlFor="meta_title" title="Meta Title" />
                  <Input
                    id="meta_title"
                    name="meta_title"
                    type="text"
                    placeholder="Enter Meta Title"
                    onChange={handleChange}
                    value={values.meta_title}
                    className={`shadow-none ${
                      errors?.meta_title && touched.meta_title
                        ? "error"
                        : "correct"
                    }`}
                  />
                  {touched.meta_title && errors?.meta_title && (
                    <ValidationFeedback title={errors?.meta_title} />
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="meta_description"
                    title="Meta Description"
                  />
                  <TextArea
                    id="meta_description"
                    name="meta_description"
                    type="text"
                    placeholder="Enter Meta Description"
                    onChange={handleChange}
                    value={values.meta_description}
                    className={`shadow-none ${
                      errors?.meta_description && touched.meta_description
                        ? "error"
                        : "correct"
                    }`}
                    rows="4"
                  />
                  {touched.meta_description && errors?.meta_description && (
                    <ValidationFeedback title={errors?.meta_description} />
                  )}
                </Col>
              </Row>

              <Row>
                <Col md={12} className="mb-4">
                  <CommonFormLabel
                    htmlFor="meta_keyword"
                    title="Meta Keywords"
                  />
                  <TextArea
                    id="meta_keyword"
                    name="meta_keyword"
                    type="text"
                    placeholder="Enter Meta Keywords"
                    onChange={handleChange}
                    value={values.meta_keyword}
                    className={`shadow-none ${
                      errors?.meta_keyword && touched.meta_keyword
                        ? "error"
                        : "correct"
                    }`}
                    rows="4"
                  />
                  {touched.meta_keyword && errors?.meta_keyword && (
                    <ValidationFeedback title={errors?.meta_keyword} />
                  )}
                </Col>
              </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Image</h4>
                <div className="mb-4">
                <CommonFormLabel htmlFor="blog_image" title="Upload New Image" />
                <FileInput
                    name="blog_image"
                    onChange={(file) => handleImageChange(file, setFieldValue)}
                    error={errors?.blog_image}
                    touched={touched.blog_image}
                    inputType="image"
                />
                {touched.blog_image && errors?.blog_image && (
                    <ValidationFeedback title={errors?.blog_image} />
                )}
                </div>

                <Row>
                {newImagePreview && (
                  <Col md={6} className="">
                    <CommonFormLabel
                      htmlFor="new_selected_image"
                      title="New selected Image"
                    />
                    <div>
                      <img
                        src={newImagePreview}
                        alt="New Selected"
                        className="img-fluid rounded border"
                        style={{ width: "200px", height: "auto" }}
                      />
                    </div>
                  </Col>
                )}

                <Col md={6} className="">
                    <CommonFormLabel
                        htmlFor="blog_image_preview"
                        title="Uploaded Image"
                    />
                    <div>
                    {blog?.blog_image && (
                        <img
                            src={`${blog?.blog_image}`}    
                            alt="Uploaded Blog"
                            className="img-fluid rounded border"
                            style={{ width: "200px", height: "auto" }}
                        />
                        ) }
                    </div>
                </Col>
                </Row>
            </Card>

            <Card className="bg-white p-4 rounded-2 shadow-sm mb-4">
            <h4 className="mb-4 d-flex justify-content-center">Video</h4>
              <Row>
                  <Col md={6} className="mb-4">
                      <CommonFormLabel htmlFor="video_link" title="Video Link (optional)" />
                      <Input
                      id="video_link"
                      name="video_link"
                      type="text"
                      placeholder="Enter Video Link"
                      onChange={handleChange}
                      value={values.video_link}
                      className={`shadow-none ${
                          errors?.video_link && touched.video_link ? "error" : "correct"
                      }`}
                      />
                      {touched.video_link && errors?.video_link && (
                      <ValidationFeedback title={errors?.video_link} />
                      )}
                  </Col>
                </Row>
                {/* <div className="mb-4">
                <CommonFormLabel htmlFor="blog_video" title="Upload New Video (optional)" />
                <FileInput
                  name="blog_video"
                  onChange={(file) => handleVideoChange(file, setFieldValue)}
                  error={errors?.blog_video}
                  touched={touched.blog_video}
                  inputType="video"
                />
                {touched.blog_video && errors?.blog_video && (
                  <ValidationFeedback title={errors?.blog_video} />
                )}
                </div>

                <Row>
                  {newVideoPreview && (
                    <Col md={6} className="">
                      <CommonFormLabel
                        htmlFor="new_selected_video"
                        title="New selected Video"
                      />
                      <div className="">
                        <video
                          controls
                          src={newVideoPreview}
                          style={{
                            marginTop: "10px",
                            width: "350px",
                            height: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </Col>
                  )}

                  <Col md={6} className="">
                    <CommonFormLabel
                        htmlFor="blog_video_preview"
                        title="Uploaded Video"
                    />
                    <div>
                    {blog?.blog_video && (
                        <video
                          controls
                          src={blog.blog_video}
                          style={{
                            marginTop: "10px",
                            width: "350px",
                            height: "auto",
                            border: "1px solid #ddd",
                            borderRadius: "5px",
                          }}
                        >
                          Your browser does not support the video tag.
                        </video>
                        ) }
                    </div>
                  </Col>
                </Row> */}
            </Card>

            <div className="d-flex align-items-center justify-content-end gap-3 common-popup-btn">
              <CommonButton
                title={isSubmitting ? "Updating..." : "Update"}
                type="submit"
                disabled={isSubmitting}
                className="btn-primary primary-shadow"
              />
              <CommonButton
                title="Cancel"
                onClick={handleCancel}
                className="btn-muted"
              />
            </div>
          </Form>
        )}
      </Formik>
      </>
  );
}